import { DEFAULT_HELP_CATEGORY_ICON, HELP_ASSISTANT_MAX_MESSAGE_LENGTH, HELP_ASSISTANT_MAX_MESSAGES, HELP_CATEGORY_ICONS } from '@/app/helpArticle/_enums';

export const helpCategoryPath = (categorySlug) => `/help/${categorySlug}`;

// Slugs are the HubSpot article paths, URL-safe as written. Eight contain a `/`,
// which stays literal here and is matched by the route's `:slug+`.
export const helpArticlePath = (slug) => `/help/article/${slug}`;

export const getHelpCategoryIcon = (categorySlug) => HELP_CATEGORY_ICONS[categorySlug] ?? DEFAULT_HELP_CATEGORY_ICON;

/** The categories a reader can open: one with no articles yet is not listed. */
export const getVisibleHelpCategories = (categories) => categories.query.map((slug) => categories.content[slug]).filter((category) => category.articles.length > 0);

/**
 * The articles either side of `slug` in its category's `articles`, in the category's order,
 * each null at an end of the list. Both are null for a slug the list does not hold.
 */
export const getAdjacentHelpArticles = (articles, slug) => {
  const index = articles.findIndex((article) => article.slug === slug);
  if (index === -1) {
    return { previous: null, next: null };
  }
  return { previous: articles[index - 1] ?? null, next: articles[index + 1] ?? null };
};

const LEADING_H1 = /^\s*# [^\n]*\n+/;

/**
 * The article body without its opening `# Title`. The page shows the title as
 * its heading, so rendering the body's own H1 would print it twice.
 */
export const stripLeadingTitle = (markdown) => markdown.replace(LEADING_H1, '');

// A step as HubSpot exported it: `3\. Select "**Custom Filters**".\` — an escaped number, so
// it is not a list, and a trailing backslash, a hard line break onto the next step.
const IMPORTED_STEP = /^(\d+)\\\.\s+(.*?)\\?\s*$/;
const HARD_BREAK = /\\\s*$/;

/**
 * The body with HubSpot's exported steps made a real numbered list. About a third of the
 * articles still carry the export's form, which renders as one long paragraph of breaks.
 * Each run of steps becomes `1. …` lines with a blank line either side, and the hard break
 * on the line that leads into the run goes with it. Anything else is left exactly as it is.
 */
export const normalizeImportedSteps = (markdown) => {
  const lines = markdown.split('\n');
  const isStep = lines.map((line) => IMPORTED_STEP.test(line));

  return lines
    .flatMap((line, index) => {
      if (!isStep[index]) {
        if (isStep[index + 1] && line.trim()) {
          return [line.replace(HARD_BREAK, ''), ''];
        }
        if (isStep[index - 1] && line.trim()) {
          return ['', line];
        }
        return [line];
      }
      const [, number, text] = line.match(IMPORTED_STEP);
      return [`${number}. ${text}`];
    })
    .join('\n');
};

/** HubSpot's own "Published 9/24/2025." footer, which the export kept as a paragraph. */
export const isPublishedNote = (text) => /^Published \d{1,2}\/\d{1,2}\/\d{4}\.?$/.test(text.trim());

/** A HubSpot-hosted video tutorial, which is shown as a button rather than an inline link. */
export const isVideoTutorialHref = (href) => /^https:\/\/play\.hubspotvideo\.com\//.test(href ?? '');

const comparable = (text) => text.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The description worth showing under an article's title, or null. Most were
 * migrated from HubSpot with the title copied in, quotes moved about, and
 * repeating it says nothing.
 */
export const getHelpArticleDescription = (article) => {
  if (!article.meta_description || comparable(article.meta_description) === comparable(article.title)) {
    return null;
  }
  return article.meta_description;
};

/**
 * The articles whose title holds every word of `filter`, ignoring case and punctuation, so
 * `copy estimate` finds `How To "Copy" An Existing Estimate`. A blank filter keeps them all.
 */
export const filterHelpArticles = (articles, filter) => {
  const words = filter.split(/\s+/).map(comparable).filter(Boolean);

  return articles.filter((article) => {
    const title = comparable(article.title);
    return words.every((word) => title.includes(word));
  });
};

/** Links that leave the app — the video tutorials, mostly — open in a new tab. */
export const isExternalHref = (href) => /^https?:\/\//i.test(href ?? '');

/** A root-relative path, such as `/help/article/<slug>`, or null for anything else. */
export const toInAppPath = (href) => (/^\/(?!\/)/.test(href ?? '') ? href : null);

/**
 * The message for a failed read: the API answers 503 when it cannot load the
 * bundle, which is the same for every read. Anything else gets the caller's own
 * `message`, so each source keeps its own.
 */
export const getHelpErrorMessage = (error, message) => (error.status === 503 ? 'The help centre is temporarily unavailable. Please try again in a few minutes.' : message);

const HUBSPOT_ARTICLE_URL = /^https?:\/\/lp\.zebel\.io\/help\/([^?#]+?)\/?(?:[?#].*)?$/i;

/**
 * The in-app path for a link to another article on the HubSpot help centre,
 * which is how articles link to each other, or null for any other link.
 */
export const toInAppHelpPath = (href) => {
  const match = HUBSPOT_ARTICLE_URL.exec(href ?? '');
  return match ? helpArticlePath(match[1]) : null;
};

/**
 * The request body's `messages` for asking `question` after `messages`, the
 * conversation so far: alternating turns, starting with the user's. Only the
 * latest turns go when it is longer than the API takes, and it still starts on
 * a question. A long answer is cut to the API's length limit, since what the
 * assistant needs from it is the gist of what it already said.
 */
export const getHelpAssistantRequestMessages = (messages, question) => {
  const turns = [...messages, { role: 'user', content: question }].map(({ role, content }) => ({ role, content: content.slice(0, HELP_ASSISTANT_MAX_MESSAGE_LENGTH) }));
  if (turns.length <= HELP_ASSISTANT_MAX_MESSAGES) {
    return turns;
  }
  // An odd count keeps the first turn a question, since the last one is.
  const keep = HELP_ASSISTANT_MAX_MESSAGES % 2 === 0 ? HELP_ASSISTANT_MAX_MESSAGES - 1 : HELP_ASSISTANT_MAX_MESSAGES;
  return turns.slice(-keep);
};

/** Whether a failed question failed because the assistant is off: the API answers 503. */
export const isHelpAssistantUnavailable = (error) => error.status === 503;
